import React, { FC } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAppSelector } from '../hooks';
import type { EconomyState } from '../types/common';

interface AwardBadgesProps {
  summary?: { key: string; count: number }[];
  size?: 'small' | 'medium';
}

/**
 * Award tally on a post card, read from the denormalized `awardSummary` the
 * server keeps on the post — no per-card request.
 */
const AwardBadges: FC<AwardBadgesProps> = ({ summary, size = 'medium' }) => {
  const theme = useTheme();
  const economy = useAppSelector((state) => state.economy as EconomyState);

  if (!summary || summary.length === 0) return null;

  const byKey = new Map((economy.config?.awards ?? []).map((a) => [a.key, a]));
  const tierColor = (key: string): string => {
    const tier = byKey.get(key)?.tier;
    if (!tier) return theme.tokens.colors.text3;
    const stops = theme.tokens.medals[tier];
    return tier === 'goat' ? theme.tokens.colors.primary : stops[stops.length - 1];
  };

  const chipHeight = size === 'small' ? 22 : 26;

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {summary.map((entry) => {
        const def = byKey.get(entry.key);
        return (
          <Tooltip key={entry.key} title={def ? `${def.name} ×${entry.count}` : entry.key}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                height: chipHeight,
                px: 0.85,
                borderRadius: 'var(--radius-pill)',
                backgroundColor: theme.tokens.colors.backgroundCardHover,
                border: `1px solid ${theme.tokens.colors.cardBorder}`,
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: size === 'small' ? 13 : 15, color: tierColor(entry.key) }} />
              <Typography
                component="span"
                sx={{
                  fontFamily: theme.tokens.typography.monoFontFamily,
                  fontSize: size === 'small' ? 11 : 12,
                  color: theme.tokens.colors.text2,
                  lineHeight: 1,
                }}
              >
                {entry.count}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default AwardBadges;
